import dayjs from 'dayjs'
import { atom } from 'jotai'

// ============================================================================
// TIME SELECTION（时间选择/预订）相关 Atoms
// ============================================================================

const createPrivateSessionSearchState = function () {
  // 当前选择的教练 ID
  const selectedInstructorIdAtom = atom('')

  const selectedSkiResortAtom = atom(null)

  // 当前选择的教练
  const instructorAtom = atom(null)

  // 可用的课程会话列表
  const availableSessionsAtom = atom([])

  // 日历显示的日期（dayjs 对象）
  const calendarDateAtom = atom(dayjs())

  // 选中的日期（字符串格式 'YYYY-MM-DD'）
  const selectedDateStringAtom = atom('')

  // 选中的时间段集合（Set<string>）
  const selectedTimeSlotsAtom = atom(new Set())

  // 按教练过滤的可用会话列表（派生状态）
  const availableSessionsByInstructorAtom = atom((get) => {
    const instructorId = get(selectedInstructorIdAtom)
    const availableSessions = get(availableSessionsAtom)
    const selectedSkiResort = get(selectedSkiResortAtom)

    if (!instructorId) {
      return []
    }

    return availableSessions
      .filter((session) => session.instructorId === instructorId)
      .filter((session) => dayjs(session.startTime).isAfter(dayjs().valueOf()))
      .filter((session) => !selectedSkiResort || session.skiResort === selectedSkiResort)
      .sort((a, b) => dayjs(a.startTime).diff(dayjs(b.startTime)))
  })

  // 寻找可用日期 (往前或往后) - 辅助函数
  const findAvailableDate = (direction, sessions, calendarDate) => {
    if (sessions.length === 0) {
      return null
    }

    const availableDates = sessions.map((session) => dayjs(session.startTime)).sort((a, b) => (direction === 'next' ? a - b : b - a))

    const currentDate = dayjs(calendarDate)
    const targetDate =
      direction === 'next'
        ? availableDates.find((d) => d.isAfter(currentDate) && !d.isSame(currentDate, 'day'))
        : availableDates.find((d) => d.isBefore(currentDate) && !d.isSame(currentDate, 'day'))
    return targetDate || null
  }

  // 下一个可用日期（派生状态）
  const nextAvailableDateAtom = atom((get) => {
    const calendarDate = get(calendarDateAtom)
    const availableSessionsByInstructor = get(availableSessionsByInstructorAtom)
    return findAvailableDate('next', availableSessionsByInstructor, calendarDate)
  })

  // 上一个可用日期（派生状态）
  const prevAvailableDateAtom = atom((get) => {
    const calendarDate = get(calendarDateAtom)
    const availableSessionsByInstructor = get(availableSessionsByInstructorAtom)
    return findAvailableDate('prev', availableSessionsByInstructor, calendarDate)
  })

  // 可用时间段列表（派生状态，基于选中日期和可用会话）
  const availableTimeSlotsAtom = atom((get) => {
    const DATE_FORMAT = 'YYYY-MM-DD'
    const TIME_FORMAT = 'HH:mm'

    const instructorId = get(selectedInstructorIdAtom)
    const selectedDateString = get(selectedDateStringAtom)
    const availableSessionsByInstructor = get(availableSessionsByInstructorAtom)

    if (!instructorId || !selectedDateString || availableSessionsByInstructor.length === 0) {
      return []
    }

    // 优化：一次性过滤和映射
    return availableSessionsByInstructor
      .filter((session) => {
        return dayjs(session.startTime).format(DATE_FORMAT) === selectedDateString
      })
      .map((session) => {
        const startTime = dayjs(session.startTime).format(TIME_FORMAT)
        const endTime = dayjs(session.endTime).format(TIME_FORMAT)
        return {
          timeSlot: `${startTime} - ${endTime}`,
          session,
        }
      })
  })

  // 选中的会话列表（派生状态，基于选中的时间段）
  const selectedSessionsAtom = atom((get) => {
    const selectedTimeSlots = get(selectedTimeSlotsAtom)
    const availableTimeSlots = get(availableTimeSlotsAtom)

    return availableTimeSlots.filter((slot) => selectedTimeSlots.has(slot.timeSlot)).map((slot) => slot.session)
  })

  // 选中会话的位置列表（派生状态）
  const selectedSessionsLocationsAtom = atom((get) => {
    const selectedSessions = get(selectedSessionsAtom)
    return Array.from(new Set(selectedSessions.map((session) => session.skiResort || session.location)))
  })

  // 可用日期集合（派生状态，用于日期禁用判断）
  const availableDateSetAtom = atom((get) => {
    const availableSessionsByInstructor = get(availableSessionsByInstructorAtom)
    return new Set(availableSessionsByInstructor.map((s) => dayjs(s.startTime).format('YYYY-MM-DD')))
  })

  // Create a new atom to clena up private session time slots state
  // I havne't decided the reset level.
  // Just reset the session selection should be enough.
  const privateSessionTimeSlotsStateResetAtom = atom(null, (get, set) => {
    // set(selectedInstructorIdAtom, '')
    // set(instructorAtom, null)
    // set(availableSessionsAtom, [])
    set(calendarDateAtom, dayjs())
    set(selectedDateStringAtom, '')
    set(selectedTimeSlotsAtom, new Set())
  })

  // 时间选择统一导出对象
  return {
    // 基础状态
    selectedInstructorId: selectedInstructorIdAtom,
    selectedSkiResort: selectedSkiResortAtom,
    instructor: instructorAtom,
    availableSessions: availableSessionsAtom,
    calendarDate: calendarDateAtom,
    selectedDateString: selectedDateStringAtom,
    selectedTimeSlots: selectedTimeSlotsAtom,
    nextAvailableDate: nextAvailableDateAtom,
    prevAvailableDate: prevAvailableDateAtom,

    // 派生状态
    availableSessionsByInstructor: availableSessionsByInstructorAtom,
    availableTimeSlots: availableTimeSlotsAtom,
    selectedSessions: selectedSessionsAtom,
    selectedSessionsLocations: selectedSessionsLocationsAtom,
    availableDateSet: availableDateSetAtom,

    // Reset
    reset: privateSessionTimeSlotsStateResetAtom,
  }
}

const privateSessionSearchStateAtom = createPrivateSessionSearchState()

export default privateSessionSearchStateAtom
export const selectedInstructorIdAtom = privateSessionSearchStateAtom.selectedInstructorId
export const selectedSkiResortAtom = privateSessionSearchStateAtom.selectedSkiResort
export const instructorAtom = privateSessionSearchStateAtom.instructor
export const availableSessionsAtom = privateSessionSearchStateAtom.availableSessions
export const calendarDateAtom = privateSessionSearchStateAtom.calendarDate
export const selectedDateStringAtom = privateSessionSearchStateAtom.selectedDateString
export const selectedTimeSlotsAtom = privateSessionSearchStateAtom.selectedTimeSlots
export const nextAvailableDateAtom = privateSessionSearchStateAtom.nextAvailableDate
export const prevAvailableDateAtom = privateSessionSearchStateAtom.prevAvailableDate
export const availableSessionsByInstructorAtom = privateSessionSearchStateAtom.availableSessionsByInstructor
export const availableTimeSlotsAtom = privateSessionSearchStateAtom.availableTimeSlots

export const selectedSessionsAtom = privateSessionSearchStateAtom.selectedSessions
export const selectedSessionsLocationsAtom = privateSessionSearchStateAtom.selectedSessionsLocations
export const availableDateSetAtom = privateSessionSearchStateAtom.availableDateSet
